'use client';

import { message } from '@tencent/tea-component';
import { getRequest, isWeMeetApp } from '@tencent/web-base';
import { create } from 'zustand';
import type { Options } from 'html2canvas';
import { getUserInfo } from '../../hooks/useLogin';
import isServer from '../../utils/env/is-server';
import { DEFAULT_DATA, fetchReport, ImageEvidence } from './api';
import { getEventIdFromLocation, UploadService } from './use-upload-file';

let html2canvas: (element: HTMLElement, options?: Partial<Options>) => Promise<HTMLCanvasElement>;

/**
 * 获取报名页截图
 */
async function getPageScreenShot(dom: HTMLElement): Promise<{
  cos_url: string;
  cos_id: string;
} | null> {
  return new Promise((resolve) => {
    const task = () => {
      try {
        html2canvas(dom, {
          backgroundColor: '#fff',
          useCORS: true,
          allowTaint: true,
        }).then((canvas) => {
          if (canvas) {
            canvas.toBlob((blob) => {
              if (blob) {
                new UploadService()
                  .uploadFile(
                    new File([blob], 'registration_page.png', {
                      type: 'image/png',
                    }),
                  )
                  .then((resp) => {
                    if (!resp.error && resp.cos_url && resp.cos_id) {
                      resolve({
                        cos_url: resp.cos_url,
                        cos_id: resp.cos_id,
                      });
                    } else {
                      console.log('html2canvas.uploadFile is error');
                      resolve(null);
                    }
                  });
              } else {
                console.log('canvas.toBlob is null');
                resolve(null);
              }
            }, 'image/png');
          } else {
            console.log('html2canvas is error');
            resolve(null);
          }
        });
      } catch (e) {
        console.log('getPageScreenShot error', e);
        resolve(null);
      }
    };
    if (!html2canvas) {
      import('html2canvas').then(mod => {
        html2canvas = mod.default;
      }).then(() => {
        task();
      }).catch(() => {
        console.log('import html2canvas error');
        resolve(null);
      });
    } else {
      task();
    }
  });
}

export const usePageReport = create<{
  visible: boolean;
  registrationPage: ImageEvidence[];
  openReport: (isLogin?: boolean) => void;
  closeReport: () => void;
  toggleVisible: () => void;
}>((set, get) => {
  const updateScreenShot = (dom = document.body) => {
    getPageScreenShot(dom).then((resp) => {
      if (resp) {
        set({
          registrationPage: [
            {
              cos_url: resp.cos_url,
              cos_id: resp.cos_id,
            },
          ],
        });
      }
    });
  };

  const openReport = (isLogin?: boolean) => {
    if (!isLogin) {
      const userInfo = getUserInfo();
      if (!userInfo) {
        const link = encodeURIComponent(window.location.pathname);
        window.location.href = `/qrcode-login.html?redirect_link=${link}`;
        return;
      }
    }

    if (!isWeMeetApp()) {
      updateScreenShot();
    }
    fetchReport(DEFAULT_DATA).then((resp) => {
      if (resp.code === 0) {
        if (isWeMeetApp()) {
          const eventId = getEventIdFromLocation();
          window.location.href = `/events/host/event/${eventId}/registration?ui=report`;
          return;
        }
        set({
          visible: true,
        });
      } else {
        message.error({ content: resp.msg });
      }
    });
  };

  const closeReport = () => {
    if (isWeMeetApp()) {
      history.back();
      return;
    }
    set({
      visible: false,
    });
  };

  const toggleVisible = () => {
    const { visible } = get();
    set({
      visible: !visible,
    });
  };

  const initVisible = () => {
    if (isServer) {
      return false;
    }
    // 从app打开报名页
    if (isWeMeetApp()) {
      const ui = getRequest('ui');
      const visible = ui === 'report';
      if (visible) {
        const dom = document.getElementById('registration-mobile-content');
        if (dom) {
          updateScreenShot(dom);
        }
      }
      return visible;
    }
    return false;
  };

  const visible = initVisible();

  return {
    registrationPage: [],
    visible,
    openReport,
    closeReport,
    toggleVisible,
  };
});
